import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Col, Form, Radio, Row, Space, Typography } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import SsoUsersRoles from '../../../../../libs/api/ssoUsersRoles';
import UIHelper from '../../../../../helpers/UIHelper';

const api = new SsoUsersRoles('permission_groups');
const apiPermissionModels = new SsoUsersRoles('permission_models');
const apiPermissions = new SsoUsersRoles('permissions');

const iconGreenStyles = {
  color: 'green',
};

const iconRedStyles = {
  color: 'red',
};

const usePremisions = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id, idModel } = useParams();
  const [premisionsModels, setPremisionsModels] = useState([]);

  const {
    data,
    singleItem: singleItem,
    loading,
    onChangeTableFilter,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
    // fetchList,
    fetchItem,
  } = useApi({
    resourceKey: 'permission_groups',
    singleResourceKey: 'permission_group',
    api,
    isMainPage: !id,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Nome'),
        dataIndex: 'title',
        width: 100,
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) => (
          <Row gutter={16} justify={'end'}>
            <Col>
              <Link to={`${pathname}/${row.id}/edit-model`}>
                <Button type={'grey'}>{t('Moduli Aperti')}</Button>
              </Link>
            </Col>
            {row.can_update !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteFilled />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [t, pathname]);
  // console.log(data);

  const columnsEditModel = useMemo(() => {
    return [
      {
        title: t('Moduli'),
        dataIndex: 'permission_model',
      },
      {
        title: t('Read'),
        dataIndex: 'access_type',
        width: 100,
        render: (data) =>
          data === 'read' ? (
            <CheckCircleOutlined style={iconGreenStyles} />
          ) : (
            <CloseCircleOutlined style={iconRedStyles} />
          ),
      },
      {
        title: t('Read and update'),
        dataIndex: 'access_type',
        width: 200,
        render: (data) =>
          data === 'write' ? (
            <CheckCircleOutlined style={iconGreenStyles} />
          ) : (
            <CloseCircleOutlined style={iconRedStyles} />
          ),
      },
      {
        title: t('Read and write'),
        dataIndex: 'access_type',
        width: 200,
        render: (data) =>
          data === 'create' ? (
            <CheckCircleOutlined style={iconGreenStyles} />
          ) : (
            <CloseCircleOutlined style={iconRedStyles} />
          ),
      },
      {
        title: t('Deny'),
        dataIndex: 'access_type',
        width: 100,
        render: (data) =>
          data === 'deny' ? (
            <CheckCircleOutlined style={iconGreenStyles} />
          ) : (
            <CloseCircleOutlined style={iconRedStyles} />
          ),
      },
      {
        title: t('Approva'),
        dataIndex: 'approve',
        width: 100,
        render: (data) =>
          data === 'deny' ? (
            <CloseCircleOutlined style={iconRedStyles} />
          ) : (
            <Space direction={'horizontal'}>
              <CheckCircleOutlined style={iconGreenStyles} />
              <Typography.Text>
                {data === 'step1' && 'Step 1'}
                {data === 'step2' && 'Step 2'}
              </Typography.Text>
            </Space>
          ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) => (
          <Row gutter={16} justify={'end'}>
            {row.can_update && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteFilled />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [t, pathname]);

  const handleCreateRole = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const data = {
          ...values,
        };
        if (!id || id === 'create') {
          await api.create({ data });
          navigate('/sso/user_roles/page/premisions');
        } else {
          await api.put(id, { data });
          navigate('/sso/user_roles/page/premisions');
        }
        // await fetchList();
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );

  useEffect(() => {
    if (
      id &&
      singleItem &&
      !pathname.includes('edit-model/new') &&
      !pathname.includes('edit-model')
    ) {
      try {
        let fieldsValues = [];
        for (let key in singleItem) {
          if (key !== 'id' && key !== 'extrastock_map') {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, singleItem, form, pathname]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { groups = [] },
        } = await apiPermissionModels.get('index_group', {
          params: {
            per: 1000,
            sortable_column: 'title',
            direction: 'asc',
          },
        });
        setPremisionsModels(groups);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const hadnleCreatePremission = async (values, idModel) => {
    let permission_model_ids = [];
    if (typeof values.permission_model_ids === 'string') {
      permission_model_ids = JSON.parse(values.permission_model_ids);
    } else {
      permission_model_ids = [values.permission_model_ids];
    }
    const newValues = {
      access_type: values.access_type,
      approve: values.approve,
      permission_model_ids,
    };
    const data = {
      ...newValues,
      permission_group_id: id,
    };
    setUpdateInProcess(true);
    form.resetFields();
    try {
      if (!idModel) {
        await apiPermissions.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await apiPermissions.put(idModel, { data });
        navigate(UIHelper.getListRoute(pathname, 2));
      }
      await fetchItem();
    } catch (e) {
      console.log(e);
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          permission_model: 'permission_model_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleSetValues = async (modelId) => {
    try {
      const {
        data: { permission = {} },
      } = await apiPermissions.get(modelId);
      let fieldsValues = [];
      for (let key in permission) {
        if (
          key !== 'id' &&
          key !== 'extrastock_map' &&
          key !== 'permission_model_id'
        ) {
          fieldsValues.push({
            name: key,
            value: permission[key],
          });
        } else if (key === 'permission_model_id') {
          fieldsValues.push({
            name: 'permission_model_ids',
            value: permission[key],
          });
        }
      }
      form.setFields(fieldsValues);
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteModelById = async (id) => {
    setUpdateInProcess(true);
    try {
      await apiPermissions.destroy(id);
      await fetchItem();
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    singleItem,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    premisionsModels,
    handleCreateRole,
    onDelete,
    hadnleCreatePremission,
    columnsEditModel,
    handleSetValues,
    handleDeleteModelById,
  };
};

export default usePremisions;
